import { useState, useEffect } from 'react';
import { api } from '../../../services/api';

interface Modele {
  id: string;
  nom: string;
  categorie: string;
  type_habit_id?: string | number;
  prix: number;
  image: string;
  description?: string;
}

interface TypeHabit {
  id: string | number;
  nom: string;
  icon?: string;
}

interface ModeleFormModalProps {
  modele: Modele | null;
  onClose: () => void;
  onSave: (data: Partial<Modele>, isEdit: boolean) => void;
}

const CATEGORIES = ['Traditionnel', 'Moderne', 'Cérémonie', 'Casual', 'Homme'];
const PRIX_DEFAUT: Record<string, number> = {
  Traditionnel: 40000, Moderne: 25000, Cérémonie: 60000, Casual: 18000, Homme: 35000,
};

export default function ModeleFormModal({ modele, onClose, onSave }: ModeleFormModalProps) {
  const [form, setForm] = useState({
    nom: '',
    categorie: 'Moderne',
    type_habit_id: '' as string | number,
    prix: 25000,
    image: '',
    description: '',
  });
  const [typesHabit, setTypesHabit] = useState<TypeHabit[]>([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (token) {
      api.getHabitTypes(token).then(setTypesHabit).catch(console.error);
    }
  }, []);

  useEffect(() => {
    if (modele) {
      setForm({
        nom: modele.nom,
        categorie: modele.categorie,
        type_habit_id: modele.type_habit_id || '',
        prix: Number(modele.prix),
        image: modele.image,
        description: modele.description || '',
      });
    }
  }, [modele]);

  const handleCategChange = (cat: string) => {
    setForm(p => ({ ...p, categorie: cat, prix: PRIX_DEFAUT[cat] || 25000 }));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    setUploading(true);
    try {
      const res = await api.uploadImage(token, file);
      if (res.url) {
        setForm(prev => ({ ...prev, image: res.url }));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = () => {
    if (!form.nom.trim()) return;
    onSave(form, !!modele);
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-playfair font-bold text-xl text-gray-900">
            {modele ? 'Modifier le modèle' : 'Nouveau modèle'}
          </h3>
          <button
            onClick={onClose}
            title="Fermer"
            className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 cursor-pointer text-gray-500 transition-colors"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="modele-nom" className="block text-xs font-semibold text-gray-700 mb-1.5">Nom du modèle *</label>
            <input
              id="modele-nom"
              type="text"
              value={form.nom}
              onChange={e => setForm(p => ({ ...p, nom: e.target.value }))}
              placeholder="ex: Boubou Grande Dame"
              title="Nom du modèle"
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-gray-50"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Catégorie</label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(c => (
                <button
                  key={c}
                  onClick={() => handleCategChange(c)}
                  className={`text-xs px-3 py-1.5 rounded-full font-medium cursor-pointer transition-colors whitespace-nowrap ${
                    form.categorie === c
                      ? 'bg-foret-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="modele-type-habit" className="block text-xs font-semibold text-gray-700 mb-1.5">Type d&apos;habit (pour tarifs main d&apos;œuvre)</label>
            <select
              id="modele-type-habit"
              value={form.type_habit_id}
              onChange={e => setForm(p => ({ ...p, type_habit_id: e.target.value }))}
              title="Type d'habit associé"
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-gray-50 cursor-pointer"
            >
              <option value="">-- Choisir un type d&apos;habit --</option>
              {typesHabit.map(t => (
                <option key={t.id} value={t.id}>{t.nom}</option>
              ))}
            </select>
            <p className="text-[10px] text-gray-400 mt-1">Lier ce modèle à un type d&apos;habit permet d&apos;automatiser le calcul de la paie du personnel.</p>
          </div>

          <div>
            <label htmlFor="modele-prix" className="block text-xs font-semibold text-gray-700 mb-1.5">Prix de base (FCFA)</label>
            <input
              id="modele-prix"
              type="number"
              value={form.prix}
              onChange={e => setForm(p => ({ ...p, prix: Number(e.target.value) }))}
              min={0}
              placeholder="0"
              title="Prix de base en FCFA"
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-gray-50"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Description (optionnel)</label>
            <textarea
              value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Détails sur le modèle, finitions, occasions…"
              rows={2}
              maxLength={500}
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-gray-50 resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Illustration du modèle</label>
            <div className="flex flex-col gap-3">
               <div className="flex items-center gap-4">
                 {form.image ? (
                   <img src={api.getProxyUrl(form.image)} alt="Aperçu" className="w-24 h-24 rounded-xl object-cover border-2 border-foret-100" />
                 ) : (
                   <div className="w-24 h-24 rounded-xl bg-gray-100 flex items-center justify-center text-gray-400 border-2 border-dashed border-gray-200">
                     <i className="ri-image-line text-3xl" />
                   </div>
                 )}
                 <div className="flex-1">
                   <label className="flex flex-col items-center justify-center h-12 px-4 bg-white text-foret-600 rounded-xl border border-foret-200 cursor-pointer hover:bg-foret-50 transition-colors text-sm font-semibold relative overflow-hidden">
                     {uploading ? (
                       <div className="flex items-center gap-2">
                         <div className="animate-spin h-4 w-4 border-2 border-foret-500 border-t-transparent rounded-full" />
                         Téléchargement...
                       </div>
                     ) : (
                       <div className="flex items-center gap-2">
                         <i className="ri-upload-cloud-2-line" />
                         Choisir une photo
                       </div>
                     )}
                     <input type="file" className="hidden" accept="image/*" onChange={handleFileUpload} disabled={uploading} />
                   </label>
                   <p className="text-[10px] text-gray-400 mt-2">Format suggéré : Carré ou Portrait (JPG, PNG)</p>
                 </div>
               </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-8">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl bg-gray-100 text-gray-600 text-sm font-semibold cursor-pointer hover:bg-gray-200 whitespace-nowrap transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={!form.nom.trim() || uploading}
            className="flex-1 px-4 py-2.5 rounded-xl bg-foret-500 text-white text-sm font-semibold cursor-pointer hover:bg-foret-600 disabled:opacity-50 whitespace-nowrap transition-colors"
          >
            {modele ? 'Enregistrer les modifications' : 'Ajouter au catalogue'}
          </button>
        </div>
      </div>
    </div>
  );
}
